// Explorer archive client — submissions to the archive server
// (archive_design.md, Version 4 amendment).
//
// The Explorer is a data-access point only: it submits a deployment's results
// into the canonical singleton and reads the server-side manifest for the
// overwrite-consent check. It never sees the local archive file — the console
// is the only place archive files are defined and opened, and the only holder
// of the file handle. All calls go through the Explorer's own Express proxy
// (/api/archive/* → archive server /archive/*), like every other Explorer
// API call.

import { getAuthHeaders } from './explorerApi';

const ARCHIVE_BASE = '/api/archive/v1';

async function parseError(response, fallback) {
    let text = '';
    try { text = await response.text(); } catch (_) { }
    let parsed = {};
    try { parsed = JSON.parse(text); } catch (_) { }
    const message = parsed?.error?.message || parsed?.message || text || response.statusText || fallback;
    const err = new Error(message);
    err.status = response.status;
    err.code = parsed?.error?.code || parsed?.error || '';
    err.data = parsed;
    return err;
}

function unwrap(json) {
    return json && json.data !== undefined ? json.data : json;
}

/**
 * Submit a contextual archive selection (for the Explorer: deployment IDs —
 * results are keyed by deployment). Resolves when the merge has landed on the
 * canonical singleton; the console's session channel takes care of the local
 * file. Throws with .code === 'NO_ARCHIVE' when no archive is open for the
 * account.
 */
export async function submitSelective(selector) {
    const response = await fetch(`${ARCHIVE_BASE}/selective`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ selector }),
    });
    if (!response.ok) throw await parseError(response, 'Archive submission failed');
    return unwrap(await response.json()); // { manifest_checksum, ...counts }
}

/**
 * Read the canonical singleton's manifest. Returns null when no archive
 * exists yet for the account.
 */
export async function fetchServerManifest() {
    const response = await fetch(`${ARCHIVE_BASE}/manifest`, { headers: getAuthHeaders() });
    if (response.status === 404) return null;
    if (!response.ok) throw await parseError(response, 'Failed to read archive manifest');
    return unwrap(await response.json()); // { manifest, manifest_checksum }
}

/**
 * Contextual archive with the author-consent overwrite gate (mirrors the
 * console's archiveWithOverwriteCheck; archive_design.md §10d/§24). Reads the
 * canonical manifest, finds which selected artifacts are already archived,
 * and asks the caller-supplied dialog before a whole-instance replace.
 * Consent is remembered in sessionStorage keyed by the canonical state plus
 * the selection, so a retry does not re-prompt; cleared on success.
 *
 * @param {object}   selector   { deployment_ids: [...] } (results, keyed by deployment)
 * @param {Function} onProgress optional ({stage, percent})
 * @param {object}   opts       { confirmOverwrite(collisions)->Promise<bool> }
 */
export async function archiveWithOverwriteCheck(selector, onProgress, opts = {}) {
    const { confirmOverwrite } = opts;

    if (onProgress) onProgress({ stage: 'checking', percent: 5 });
    let manifest = null;
    let serverChecksum = '';
    try {
        const res = await fetchServerManifest();
        manifest = res?.manifest || null;
        serverChecksum = res?.manifest_checksum || '';
    } catch (_) { manifest = null; }

    const existingResults = new Set(manifest?.deployment_ids || []);
    const nameByID = {};
    (manifest?.contents || []).forEach(c => { if (c && c.id) nameByID[c.id] = c.name || c.id; });

    const collisions = (selector.deployment_ids || [])
        .filter(id => existingResults.has(id))
        .map(id => ({ id, type: 'result', name: nameByID[id] || id }));

    if (collisions.length > 0 && typeof confirmOverwrite === 'function') {
        const selectionIDs = (selector.deployment_ids || []).slice().sort();
        const ssKey = `overwriteOK:${serverChecksum}:${selectionIDs.join(',')}`;
        let approved = false;
        try { approved = sessionStorage.getItem(ssKey) === '1'; } catch (_) { }
        if (!approved) {
            const ok = await confirmOverwrite(collisions);
            if (!ok) {
                const err = new Error('Overwrite declined by author');
                err.canceled = true;
                throw err;
            }
            try { sessionStorage.setItem(ssKey, '1'); } catch (_) { }
        }
        if (onProgress) onProgress({ stage: 'merging', percent: 25 });
        const res = await submitSelective(selector);
        try { sessionStorage.removeItem(ssKey); } catch (_) { }
        if (onProgress) onProgress({ stage: 'done', percent: 100 });
        return res;
    }

    if (onProgress) onProgress({ stage: 'merging', percent: 25 });
    const res = await submitSelective(selector);
    if (onProgress) onProgress({ stage: 'done', percent: 100 });
    return res;
}

/**
 * List the org's deployments (GET /api/deployments, org-wide when no
 * survey_id is given). The Archiver panel joins these against the survey list
 * for display; a deployment is archivable once it is closed (is_open false).
 */
export async function fetchAllDeployments() {
    const response = await fetch('/api/deployments', { headers: getAuthHeaders() });
    if (!response.ok) throw await parseError(response, 'Failed to list deployments');
    const body = unwrap(await response.json());
    return Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
}

/**
 * Canonical-archive status for the Results Archiver strip
 * (GET /api/archive/v1/status → archive server). Always returns an object:
 * { open, file_name, adopted_at, last_merge, totals }. open:false means no
 * archive is currently open for the account.
 */
export async function fetchArchiveStatus() {
    const response = await fetch(`${ARCHIVE_BASE}/status`, { headers: getAuthHeaders() });
    if (!response.ok) throw await parseError(response, 'Failed to read archive status');
    return unwrap(await response.json());
}

const WORKSPACE_BASE = '/api/workspace';

/**
 * Per-deployment content counts + latest-archive reference, keyed by
 * deployment_id (GET /api/workspace/deployment-stats → sam-dbam).
 */
export async function fetchDeploymentStats() {
    const response = await fetch(`${WORKSPACE_BASE}/deployment-stats`, { headers: getAuthHeaders() });
    if (!response.ok) throw await parseError(response, 'Failed to load deployment stats');
    const body = unwrap(await response.json());
    return Array.isArray(body) ? body : [];
}

/**
 * Account-wide archive history — only real merges (operations that wrote
 * provenance rows), collapsed to the newest per deployment
 * (GET /api/workspace/archive-history → sam-dbam).
 */
export async function fetchArchiveHistory() {
    const response = await fetch(`${WORKSPACE_BASE}/archive-history`, { headers: getAuthHeaders() });
    if (!response.ok) throw await parseError(response, 'Failed to load archive history');
    const body = unwrap(await response.json());
    return Array.isArray(body) ? body : [];
}

/**
 * Edit notes on a workspace-archive history record
 * (PUT /api/workspace/archives/{archive_record_id}/notes → sam-dbam). This
 * targets workspace_archives, not the legacy survey-archive notes route.
 */
export async function updateWorkspaceArchiveNotes(archiveRecordId, notes) {
    const response = await fetch(`${WORKSPACE_BASE}/archives/${archiveRecordId}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ notes }),
    });
    if (!response.ok) throw await parseError(response, 'Failed to update notes');
    return unwrap(await response.json());
}
